<script setup lang="ts">
import {
  apps,
  formatting,
  LegacyOrder,
  legacyOrderKey,
  LegacyPayment,
  useLocalization,
  usePhrases
} from '@hantera/portal-app';
import { ContextMenuButton, DropdownButton, DropdownItem, Panel } from '@hantera/design-system';
import AppSlotView from '@/AppSlotView.vue';
import { computed, inject, ref } from 'vue';
import useBackend from '@/composition/useBackend';
import Decimal from 'decimal.js';
import { PaymentRetryType, PaymentState } from '@/backend/legacy-orders';

const props = defineProps<{
  data: LegacyPayment
}>();

// There's a dependency on order here as captures are stored on invoices in the legacy model.
// In the new model captures will be on the payment instead so there will be no order dependency.
const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const { currency, dateTime } = formatting;
const backend = useBackend();
const t = useLocalization();
const phrases = usePhrases('legacyOrderView.paymentMethods')

const balance = computed(() => props.data.reservedAmount.add(props.data.paidAmount).sub(props.data.refundedAmount).sub(props.data.clearedAmount));
const description = computed(() => props.data.description.replaceAll('\r\n', '<br>'));

const reservedAmount = computed(() => {
  return order.value.ledger
    .filter(r => r.actionType === 'Reservation' && r.paymentExternalId === props.data.externalId && r.amount.gt(0))
    .reduce((res, r) => res.add(r.amount), new Decimal(0));
})

// HACK This is at best a simulation of how the future payment framework will work and keep track of captures and refunds
const captures = computed(() => {
  return order.value.invoices.flatMap(r => r.captures.map(c => ({
    capture: c,
    invoice: r
  }))).filter(r => r.capture.payment?.paymentId === props.data.paymentId);
});

async function retry(type: PaymentRetryType) {
  await backend.legacyOrders.retryPayment(order.value.orderId, props.data.externalId, type);
}
async function setState(state: PaymentState) {
  await backend.legacyOrders.setPaymentState(order.value.orderId, props.data.externalId, state);
}

// HACK: This is a super ugly hack to get the transaction reference for Paypal. This will be replaced by the new payment framework.
const transactionReference = computed(() => {
  var entry = order.value.ledger.find(r => r.transactionExternalId);
  if (entry) {
    return entry.transactionExternalId;
  }
  return undefined;
})

const cancelledTimestamp = computed(() => {
  let timestamp = order.value.stateTransitions.find(r => r.toState === 'Cancelled')?.timestampUtc;
  if (timestamp) {
    return dateTime(timestamp);
  }
  return undefined;
});
</script>

<template>
  <Panel
    class="legacy-payment-details"
    :data-highlight="`legacy-payment-details__${data.paymentId}`"
  >
    <div class="legacy-payment-details__header">
      <div class="legacy-payment-details__header-details">
        <div class="legacy-payment-details__title">
          <div class="legacy-payment-details__number">{{ data.paymentNumber }}</div>
          <div class="legacy-payment-details__method">{{ phrases(data.methodName) }}</div>
        </div>
      </div>
      <div>
        <ContextMenuButton class="legacy-payment-details__context-menu">
          <AppSlotView
            :slot="apps.legacySlots.legacyPayment.contextMenu"
            :context="{
              payment: data
            }"
          />
        </ContextMenuButton>
      </div>
    </div>
    <div class="legacy-payment-details__content">
      <div class="legacy-payment-details__amounts box-content p">
        <div class="amount-label">
          {{ t('payment.reserved') }}
        </div>
        <div class="amount-label">
          {{ t('payment.charged') }}
        </div>
        <div class="amount-label">
          {{ t('payment.refunded') }}
        </div>
        <div class="amount-label">
          {{ t('payment.cleared') }}
        </div>
        <div class="legacy-payment-details__balance amount-label">
          {{ t('payment.balance') }}
        </div>

        <div>
          {{ currency(data.reservedAmount, order.currencyCode) }}
        </div>
        <div>
          {{ currency(data.paidAmount, order.currencyCode) }}
        </div>
        <div>
          {{ currency(data.refundedAmount, order.currencyCode) }}
        </div>
        <div>
          {{ currency(data.clearedAmount, order.currencyCode) }}
        </div>
        <div class="legacy-payment-details__balance">
          {{ currency(balance, order.currencyCode) }}
        </div>
      </div>
      <div class="legacy-payment-details__fields box-content">
        <div class="legacy-payment-details__field">
          <diV class="legacy-payment-details__field-name">
            External Reference
          </diV>
          <div class="legacy-payment-details__field-value">
            {{ data.externalId }}
          </div>
        </div>
        <div
          class="legacy-payment-details__field"
          v-if="transactionReference"
        >
          <diV class="legacy-payment-details__field-name">
            Transaction Reference
          </diV>
          <div class="legacy-payment-details__field-value">
            {{ transactionReference }}
          </div>
        </div>
      </div>
      <div class="box-content p">
        <div class="legacy-payment-details__captures">
          <div
            v-if="reservedAmount.gt(0)"
            class="legacy-payment-details__capture"
          >
            <div>
              <div class="legacy-payment-details__capture-status legacy-payment-details__capture-status--ok"></div>
              {{ t('payment.reservation') }} {{ dateTime(order.createdUtc) }}
            </div>
            <div class="legacy-payment-details__capture-amount">
              {{ currency(reservedAmount, order.currencyCode) }}
            </div>
          </div>
          <div
            v-if="order.state === 'Cancelled'"
            class="legacy-payment-details__capture"
          >
            <div>
              <div
                class="legacy-payment-details__capture-status legacy-payment-details__capture-status--ok"
                :class="{
                  'legacy-payment-details__capture-status--failed': data.reservedAmount.gt(0),
                  'legacy-payment-details__capture-status--ok': data.reservedAmount.eq(0),
                }"
              ></div>
              {{ t('payment.cancellation') }} {{ cancelledTimestamp }}
              <div class="legacy-payment-details__retry">
                <div
                  class="btn-group"
                  v-if="data.reservedAmount.gt(0)"
                >
                  <button
                    @click="retry(PaymentRetryType.cancellation)"
                    class="btn-primary"
                  >
                    {{ t('payment.retry') }}
                  </button>
                  <DropdownButton
                    :style="'primary'"
                    :label="t('payment.retry')"

                  >
                    <DropdownItem @click="setState(PaymentState.cancelled)">{{
                      t(`payment.setState`, {
                        state: PaymentState.cancelled
                      }) }}</DropdownItem>
                  </DropdownButton>
                </div>
              </div>
            </div>
            <div class="legacy-payment-details__capture-amount">
              {{ currency(reservedAmount.neg(), order.currencyCode) }}
            </div>
          </div>
          <div
            v-for="entry in captures"
            :key="entry.capture.captureExternalId"
            class="legacy-payment-details__capture"
          >
            <div>
              <div
                :class="`legacy-payment-details__capture-status legacy-payment-details__capture-status--${entry.capture.isSuccessful ? 'ok' : 'failed'}`"
              ></div>
              {{ entry.capture.amount.gt(0) ? t('payment.capture') : t('payment.refund') }} {{
                dateTime(entry.invoice.createdUtc) }}
              <div class="legacy-payment-details__retry">
                <div
                  class="btn-group"
                  v-if="!entry.capture.isSuccessful"
                >
                  <button
                    @click="retry(entry.capture.amount.gt(0) ? PaymentRetryType.capture : PaymentRetryType.refund)"
                    class="btn-primary"
                  >
                    {{ t('payment.retry') }}
                  </button>
                  <div>
                    <DropdownButton
                      :style="'primary'"
                    >
                      <DropdownItem
                        @click="setState(entry.capture.amount.gt(0) ? PaymentState.paid : PaymentState.refunded)"
                      >{{
                        t(`payment.setState`, {
                          state: entry.capture.amount.gt(0) ? PaymentState.paid :
                            PaymentState.refunded
                        }) }}</DropdownItem>
                    </DropdownButton>
                  </div>
                </div>
              </div>
            </div>
            <div class="legacy-payment-details__capture-amount">
              {{ currency(entry.capture.amount, order.currencyCode) }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="legacy-payment-details__events box-content p"
        v-html="description"
      >
      </div>
    </div>
  </Panel>
</template>

<style scoped>
.amount-label {
  font-size: 12px;
}
</style>

<style>
.legacy-payment-details {
  max-width: 800px;
}

.legacy-payment-details__header {
  position: relative;
  padding-left: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.legacy-payment-details__header-details {
  flex-grow: 1;
}

.legacy-payment-details__title {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  gap: 10px;
  align-items: center;
}

.legacy-payment-details__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.legacy-payment-details__method {
  white-space: nowrap;
}

.legacy-payment-details__context-menu {
  height: 32px;
  width: 32px;
}

.legacy-payment-details__content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

.legacy-payment-details__amounts {
  grid-column: 1 / 3;
}

.legacy-payment-details__amounts {
  display: grid;
  grid-template-columns: repeat(5, min-content);
  text-align: right;
  justify-content: space-between;
}

.legacy-payment-details__balance {
  font-weight: 600;
}

.legacy-payment-details__fields.panel-content {
  display: grid;
  grid-auto-flow: row;
  padding: 0;
}

.legacy-payment-details__field {
  border-bottom: 1px solid hsl(var(--color-70));
  padding: 5px;
}

.legacy-payment-details__field:last-child {
  border-bottom: none;
}

.legacy-payment-details__field-name {
  font-size: 12px;
}

.legacy-payment-details__captures {
  display: grid;
  grid-template-columns: auto min-content;
  grid-auto-rows: min-content;
  gap: 5px;
}

.legacy-payment-details__capture {
  display: contents;
}

.legacy-payment-details__retry {
  display: inline-block;
}

.legacy-payment-details__capture-status {
  display: inline-block;
  width: 5px;
  height: 30px;
  border-radius: 4px;
  vertical-align: middle;
}

.legacy-payment-details__capture-status--ok {
  background-color: #1eb640;
}

.legacy-payment-details__capture-status--failed {
  background-color: #da0f0f;
}

.legacy-payment-details__capture-amount {
  text-align: right;
  align-self: center;
}

.legacy-payment-details__events {
  grid-column: 1 / 3;
}

@container (max-width: 550px) {
  .legacy-payment-details__content {
    display: flex;
    flex-direction: column;
  }

  .legacy-payment-details__title {
    grid-template-columns: min-content auto;
  }

  .legacy-payment-details__method {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>